import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import type {
  User,
  AuthTokens,
  LoginCredentials,
  RegisterData,
  AuthResponse,
  AuthenticatedAuthResponse,
  AuthenticatedRegisterResult,
  PendingRegisterResult,
  PendingRegistrationResponse,
  RefreshTokenResponse,
  RegisterResult,
  RegistrationSelectedPlan,
  UserPreferences,
} from "@/types";
import { UserRole, UserStatus } from "@/types";
import { config } from "@/config";
import { setPlatformAdminEntitlementSnapshot } from "@/utils";
import { reconnectAgentSocket } from "@/services/api/agentSocketClient";
import { getPublicAuthChallengeToken } from "@/services/api/publicAuthChallenge";
import { normalizeAuthTokens } from "@/schemas/auth";
import {
  clearInMemoryAuthTokens,
  getInMemoryAuthTokens,
  setInMemoryAuthTokens,
} from "@/stores/auth/token-memory";
import type { BillingInterval } from "@/types/billing";
import {
  authDebug,
  authDebugWarn,
  describeAuthError,
  installAuthDebugHelpers,
} from "@/stores/auth/auth-diagnostics";

// Auth storage helpers - keep non-token auth data in sessionStorage only.
const REMEMBER_ME_KEY = "terramatrix_remember_me";
const ORG_CONTEXT_STORAGE_KEY = "terramatrix:org-context";
const USER_ROLE_VALUES: string[] = Object.values(UserRole);
const USER_STATUS_VALUES: string[] = Object.values(UserStatus);
const REGISTRATION_PLAN_TIERS = new Set<string>([
  "free",
  "basic",
  "standard",
  "pro",
] satisfies readonly RegistrationSelectedPlan["tier"][]);
const REGISTRATION_BILLING_INTERVALS = new Set<string>([
  "monthly",
  "annual",
]);

const isRegistrationTier = (
  value: unknown,
): value is RegistrationSelectedPlan["tier"] =>
  typeof value === "string" && REGISTRATION_PLAN_TIERS.has(value);

const isBillingInterval = (value: unknown): value is BillingInterval =>
  typeof value === "string" && REGISTRATION_BILLING_INTERVALS.has(value);

async function getApiClient() {
  const { api } = await import("@/services/api/client");
  return api;
}

async function getProfileService() {
  const { ProfileService } = await import("@/services/api/profileService");
  return ProfileService;
}

function isObjectLike(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function coerceAuthTokens(value: unknown): AuthTokens | null {
  const normalized = normalizeAuthTokens(value);
  return normalized
    ? {
        ...normalized,
        refreshToken: "",
      }
    : null;
}

function readAndRemoveLegacyTokens(
  storage: Storage | undefined,
): AuthTokens | null {
  if (!storage) {
    return null;
  }

  try {
    const item = storage.getItem(config.storage.keys.tokens);
    storage.removeItem(config.storage.keys.tokens);

    if (!item) {
      return null;
    }

    const parsed: unknown = JSON.parse(item);
    return coerceAuthTokens(parsed);
  } catch {
    try {
      storage.removeItem(config.storage.keys.tokens);
    } catch {
      // Ignore cleanup failures in unavailable browser storage.
    }
    return null;
  }
}

function takeLegacyStoredAuthTokens(): AuthTokens | null {
  if (typeof window === "undefined") {
    return null;
  }

  const sessionTokens = readAndRemoveLegacyTokens(window.sessionStorage);
  try {
    window.localStorage.removeItem(config.storage.keys.tokens);
  } catch {
    // Ignore cleanup failures in unavailable browser storage.
  }
  return sessionTokens;
}

function toDate(value: unknown): Date | undefined {
  if (value instanceof Date) {
    return value;
  }

  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  return undefined;
}

function readOptionalString(
  source: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = Reflect.get(source, key);
  return typeof value === "string" ? value : undefined;
}

function readNullableString(
  source: Record<string, unknown>,
  key: string,
): string | null | undefined {
  const value = Reflect.get(source, key);

  if (value === null) {
    return null;
  }

  return typeof value === "string" ? value : undefined;
}

function readBoolean(
  source: Record<string, unknown>,
  key: string,
  fallback: boolean,
): boolean {
  const value = Reflect.get(source, key);
  return typeof value === "boolean" ? value : fallback;
}

function readNumber(
  source: Record<string, unknown>,
  key: string,
  fallback: number,
): number {
  const value = Reflect.get(source, key);
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function parseUserRole(value: unknown): UserRole | null {
  if (typeof value !== "string" || !USER_ROLE_VALUES.includes(value)) {
    return null;
  }

  switch (value) {
    case UserRole.ADMIN:
      return UserRole.ADMIN;
    case UserRole.USER:
      return UserRole.USER;
    case UserRole.VIEWER:
      return UserRole.VIEWER;
    case UserRole.MODERATOR:
      return UserRole.MODERATOR;
    default:
      return null;
  }
}

function parseUserStatus(value: unknown): UserStatus | null {
  if (typeof value !== "string" || !USER_STATUS_VALUES.includes(value)) {
    return null;
  }

  switch (value) {
    case UserStatus.ACTIVE:
      return UserStatus.ACTIVE;
    case UserStatus.INACTIVE:
      return UserStatus.INACTIVE;
    case UserStatus.SUSPENDED:
      return UserStatus.SUSPENDED;
    case UserStatus.PENDING:
      return UserStatus.PENDING;
    default:
      return null;
  }
}

function parseUserTheme(value: unknown): UserPreferences["theme"] {
  switch (value) {
    case "dark":
      return "dark";
    case "system":
      return "system";
    case "light":
    default:
      return "light";
  }
}

function parseUserPreferences(value: unknown): UserPreferences | null {
  if (!isObjectLike(value)) {
    return null;
  }

  const notifications = Reflect.get(value, "notifications");
  const notificationSettings = isObjectLike(notifications) ? notifications : {};
  const editor = Reflect.get(value, "editor");
  const editorSettings = isObjectLike(editor) ? editor : {};
  const homeProjectId = readNullableString(value, "homeProjectId");
  const homePageId = readNullableString(value, "homePageId");
  const preferences: UserPreferences = {
    theme: parseUserTheme(Reflect.get(value, "theme")),
    language: readOptionalString(value, "language") ?? "en",
    notifications: {
      email: readBoolean(notificationSettings, "email", true),
      desktop: readBoolean(notificationSettings, "desktop", true),
      collaboration: readBoolean(notificationSettings, "collaboration", true),
    },
    editor: {
      snapToGrid: readBoolean(editorSettings, "snapToGrid", true),
      showGrid: readBoolean(editorSettings, "showGrid", true),
      gridSize: readNumber(editorSettings, "gridSize", 10),
      autoSave: readBoolean(editorSettings, "autoSave", true),
      autoSaveInterval: readNumber(editorSettings, "autoSaveInterval", 30000),
    },
  };

  if (homeProjectId !== undefined) {
    preferences.homeProjectId = homeProjectId;
  }

  if (homePageId !== undefined) {
    preferences.homePageId = homePageId;
  }

  const connections = Reflect.get(value, "connections");
  if (isObjectLike(connections)) {
    const acceptRequests = Reflect.get(connections, "acceptRequests");
    preferences.connections = {
      searchableByEmail: readBoolean(connections, "searchableByEmail", true),
      searchableByUsername: readBoolean(
        connections,
        "searchableByUsername",
        true,
      ),
      acceptRequests: acceptRequests === "nobody" ? "nobody" : "anyone",
    };
  }

  const messaging = Reflect.get(value, "messaging");
  if (isObjectLike(messaging)) {
    preferences.messaging = {
      allowFromNonConnections: readBoolean(
        messaging,
        "allowFromNonConnections",
        false,
      ),
      showReadReceipts: readBoolean(messaging, "showReadReceipts", true),
      showOnlineStatus: readBoolean(messaging, "showOnlineStatus", true),
    };
  }

  return preferences;
}

function fetchProjectAdminStatusInBackground(
  fetchProjectAdminStatus: () => Promise<void>,
  event: string,
): void {
  void fetchProjectAdminStatus().catch((error) => {
    authDebugWarn(event, describeAuthError(error));
  });
}

function applyServerPlatformAdmin(user: User, isPlatformAdmin: boolean): User {
  return {
    ...user,
    isPlatformAdmin,
  };
}

function applyPayloadPlatformAdmin(user: User): User {
  return applyServerPlatformAdmin(user, user.isPlatformAdmin === true);
}

function applyAuthoritativePlatformAdmin(
  user: User,
  isPlatformAdmin: boolean,
): User {
  return applyServerPlatformAdmin(user, isPlatformAdmin);
}

function getResponseEmail(value: AuthResponse, fallback: string): string {
  const responseEmail = Reflect.get(value, "email");
  if (typeof responseEmail === "string" && responseEmail.trim() !== "") {
    return responseEmail;
  }

  const user = Reflect.get(value, "user");
  if (isObjectLike(user)) {
    const email = Reflect.get(user, "email");
    if (typeof email === "string" && email.trim() !== "") {
      return email;
    }
  }

  return fallback;
}

function readSelectedPlan(
  value: unknown,
): RegistrationSelectedPlan | undefined {
  if (!isObjectLike(value)) {
    return undefined;
  }

  const tier = Reflect.get(value, "tier");
  if (!isRegistrationTier(tier)) {
    return undefined;
  }

  if (tier === "free") {
    return { tier: "free" };
  }

  const billingInterval = Reflect.get(value, "billingInterval");
  if (!isBillingInterval(billingInterval)) {
    return undefined;
  }

  return {
    tier,
    billingInterval,
  };
}

function isPendingRegistrationResponse(
  value: AuthResponse,
): value is PendingRegistrationResponse {
  if (
    value.verificationRequired === true ||
    value.pendingVerification === true
  ) {
    return true;
  }

  if (
    value.status === "pending" ||
    value.status === "pending_verification" ||
    value.status === "verification_required"
  ) {
    return true;
  }

  const tokens = Reflect.get(value, "tokens");
  if (tokens !== undefined && tokens !== null) {
    return false;
  }

  const user = Reflect.get(value, "user");
  if (!isObjectLike(user)) {
    return false;
  }

  return Reflect.get(user, "status") === UserStatus.PENDING;
}

interface HydrateStoredUserOptions {
  trustPlatformAdmin?: boolean;
}

function hydrateStoredUser(
  value: unknown,
  options: HydrateStoredUserOptions = {},
): User | null {
  if (!isObjectLike(value)) {
    return null;
  }

  const id = Reflect.get(value, "id");
  const email = Reflect.get(value, "email");
  const username = Reflect.get(value, "username");
  const firstName = Reflect.get(value, "firstName");
  const lastName = Reflect.get(value, "lastName");
  const role = parseUserRole(Reflect.get(value, "role"));
  const status = parseUserStatus(Reflect.get(value, "status"));
  const preferences = parseUserPreferences(Reflect.get(value, "preferences"));

  if (
    typeof id !== "string" ||
    typeof email !== "string" ||
    typeof username !== "string" ||
    typeof firstName !== "string" ||
    typeof lastName !== "string" ||
    role === null ||
    status === null ||
    preferences === null
  ) {
    return null;
  }

  const createdAt = toDate(Reflect.get(value, "createdAt")) ?? new Date(0);
  const updatedAt =
    toDate(Reflect.get(value, "updatedAt")) ??
    toDate(Reflect.get(value, "createdAt")) ??
    createdAt;
  const emailVerifiedAt = toDate(Reflect.get(value, "emailVerifiedAt"));
  const lastLoginAt = toDate(Reflect.get(value, "lastLoginAt"));
  const backendId = readOptionalString(value, "_id");
  const organizationId = readOptionalString(value, "organizationId");
  const avatar = readOptionalString(value, "avatar");
  const bio = readOptionalString(value, "bio");
  const phone = readOptionalString(value, "phone");
  const isPlatformAdmin = Reflect.get(value, "isPlatformAdmin");

  const user: User = {
    id,
    email,
    username,
    firstName,
    lastName,
    role,
    status,
    preferences,
    createdAt,
    updatedAt,
  };

  if (backendId !== undefined) {
    user._id = backendId;
  }

  if (organizationId !== undefined) {
    user.organizationId = organizationId;
  }

  if (avatar !== undefined) {
    user.avatar = avatar;
  }

  if (bio !== undefined) {
    user.bio = bio;
  }

  if (phone !== undefined) {
    user.phone = phone;
  }

  if (emailVerifiedAt) {
    user.emailVerifiedAt = emailVerifiedAt;
  }

  if (lastLoginAt) {
    user.lastLoginAt = lastLoginAt;
  }

  if (
    options.trustPlatformAdmin !== false &&
    typeof isPlatformAdmin === "boolean"
  ) {
    user.isPlatformAdmin = isPlatformAdmin;
  }

  return user;
}

function stripPersistedUserAuthorizationFields<T>(
  key: string,
  value: T,
): unknown {
  if (key !== config.storage.keys.user || !isObjectLike(value)) {
    return value;
  }

  const { isPlatformAdmin: _isPlatformAdmin, ...persistableUser } = value;
  return persistableUser;
}

interface AuthMeResponse {
  user?: User | null;
  isPlatformAdmin?: boolean;
  isProjectAdmin?: boolean;
  projectAdminIds?: string[];
  isOrgAdmin?: boolean;
  isServiceProvider?: boolean;
  isInstaller?: boolean;
  serviceProviderProfileId?: string | null;
}

const authStorage = {
  // Check if "Remember Me" was selected
  isRememberMe(): boolean {
    if (typeof window === "undefined" || !window.localStorage) return false;
    return window.localStorage.getItem(REMEMBER_ME_KEY) === "true";
  },

  // Set "Remember Me" preference
  setRememberMe(value: boolean): void {
    if (typeof window === "undefined" || !window.localStorage) return;
    if (value) {
      window.localStorage.setItem(REMEMBER_ME_KEY, "true");
    } else {
      window.localStorage.removeItem(REMEMBER_ME_KEY);
    }
  },

  // User data is non-token session state. Tokens stay memory-only.
  getStorage(): Storage | null {
    if (typeof window === "undefined") return null;
    return window.sessionStorage ?? null;
  },

  getStorageOrder(): Storage[] {
    if (typeof window === "undefined") return [];

    this.removeLegacyLocalAuth();
    const storage = this.getStorage();
    return storage ? [storage] : [];
  },

  removeLegacyLocalAuth(): void {
    if (typeof window === "undefined") return;
    window.localStorage?.removeItem(config.storage.keys.user);
    window.localStorage?.removeItem(config.storage.keys.tokens);
    window.sessionStorage?.removeItem(config.storage.keys.tokens);
  },

  // Save non-token auth data to session storage only.
  set<T>(key: string, value: T): void {
    try {
      if (key === config.storage.keys.tokens) {
        this.removeLegacyLocalAuth();
        return;
      }

      const storage = this.getStorage();
      if (storage) {
        if (typeof window !== "undefined") {
          window.localStorage?.removeItem(key);
        }
        storage.setItem(
          key,
          JSON.stringify(stripPersistedUserAuthorizationFields(key, value)),
        );
      }
    } catch (error) {
      console.warn("Failed to save to storage:", error);
    }
  },

  // Get non-token auth data from sessionStorage only.
  // Note: This generic helper returns unvalidated data — callers that need schema
  // validation (e.g., JWT payloads) must validate at their own call sites.
  get(key: string): unknown | null {
    try {
      if (key === config.storage.keys.tokens) {
        this.removeLegacyLocalAuth();
        return null;
      }

      for (const storage of this.getStorageOrder()) {
        const item = storage.getItem(key);
        if (item) {
          const parsed: unknown = JSON.parse(item);
          return parsed;
        }
      }

      return null;
    } catch (error) {
      console.warn("Failed to read from storage:", error);
      return null;
    }
  },

  // Remove auth data from both storages
  remove(key: string): void {
    try {
      if (
        key === config.storage.keys.user ||
        key === config.storage.keys.tokens
      ) {
        this.removeLegacyLocalAuth();
      }
      if (key === config.storage.keys.tokens) {
        clearInMemoryAuthTokens();
      }
      if (typeof window !== "undefined" && window.sessionStorage)
        window.sessionStorage.removeItem(key);
      if (typeof window !== "undefined" && window.localStorage)
        window.localStorage.removeItem(key);
    } catch (error) {
      console.warn("Failed to remove from storage:", error);
    }
  },

  // Clear all auth data
  clearAll(): void {
    if (typeof window !== "undefined" && window.localStorage)
      window.localStorage.removeItem(REMEMBER_ME_KEY);
    clearInMemoryAuthTokens();
    this.remove(config.storage.keys.user);
    this.remove(config.storage.keys.tokens);
    // Clear org context so it doesn't leak to the next logged-in user
    this.remove(ORG_CONTEXT_STORAGE_KEY);
  },
};

function resetOrganizationContextForAuthInvalidation(): void {
  void import("../organization/organization-store")
    .then(({ useOrganizationStore }) => {
      useOrganizationStore.getState().clearCurrentContext();
    })
    .catch((error) => {
      authDebugWarn("auth.org-context-reset-failed", describeAuthError(error));
    });
}

function clearAuthInvalidationPersistence(): void {
  authStorage.remove(config.storage.keys.user);
  authStorage.remove(config.storage.keys.tokens);
  authStorage.remove(ORG_CONTEXT_STORAGE_KEY);
  resetOrganizationContextForAuthInvalidation();
}

function isAuthDenial(error: unknown): boolean {
  if (typeof error !== "object" || error === null) {
    return false;
  }

  const directStatus = Reflect.get(error, "status");
  if (directStatus === 401 || directStatus === 403) {
    return true;
  }

  const response = Reflect.get(error, "response");
  if (typeof response !== "object" || response === null) {
    return false;
  }

  const responseStatus = Reflect.get(response, "status");
  return responseStatus === 401 || responseStatus === 403;
}

interface AuthState {
  // State
  user: User | null;
  tokens: AuthTokens | null;
  platformAdminEntitlement: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  initialized: boolean;

  // Two-Factor Authentication State
  requires2FA: boolean;
  tempUserId: string | null;
  twoFactorMethod: "2fa" | "backup_code" | null;

  // Project and org admin status
  isProjectAdmin: boolean;
  projectAdminIds: string[];
  isOrgAdmin: boolean;

  // Service provider status
  isServiceProvider: boolean;
  serviceProviderTier: string | null;

  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  verify2FA: (code: string, method?: "2fa" | "backup_code") => Promise<void>;
  cancel2FA: () => void;
  register: (data: RegisterData) => Promise<RegisterResult>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  clearError: () => void;
  setUser: (user: User) => void;
  setTokens: (tokens: AuthTokens) => void;
  reconcilePlatformAdminEntitlement: (isPlatformAdmin: boolean) => void;
  initialize: () => void | Promise<void>;
  fetchProjectAdminStatus: () => Promise<void>;
  setHomeProject: (projectId: string) => Promise<void>;
  clearHomeProject: () => Promise<void>;
  setHomePage: (projectId: string, pageId: string) => Promise<void>;
  clearHomePage: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  subscribeWithSelector(
    immer((set, get) => ({
      // Initial state
      user: null,
      tokens: null,
      platformAdminEntitlement: false,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      initialized: false,

      // Two-Factor Authentication State
      requires2FA: false,
      tempUserId: null,
      twoFactorMethod: null,

      // Project and org admin status
      isProjectAdmin: false,
      projectAdminIds: [],
      isOrgAdmin: false,

      // Service provider status
      isServiceProvider: false,
      serviceProviderTier: null,

      // Actions
      login: async (credentials: LoginCredentials) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
          state.requires2FA = false;
          state.tempUserId = null;
        });

        try {
          const api = await getApiClient();
          const data = await api.post<
            AuthenticatedAuthResponse & {
              requiresTwoFactor?: boolean;
              userId?: string;
            },
            LoginCredentials
          >("/auth/login", credentials);

          if (!data.data) {
            throw new Error("Login failed - no data received");
          }

          const loginData = data.data;
          const twoFactorUserId =
            typeof loginData.userId === "string" ? loginData.userId : null;

          // Check if 2FA is required
          if (loginData.requiresTwoFactor && twoFactorUserId) {
            authStorage.setRememberMe(credentials.rememberMe ?? false);
            set((state) => {
              state.requires2FA = true;
              state.tempUserId = twoFactorUserId;
              state.twoFactorMethod = "2fa";
              state.isLoading = false;
            });
            return; // Don't complete login - wait for 2FA verification
          }

          const { user, tokens } = loginData;
          const persistedUser = hydrateStoredUser(user);
          const normalizedTokens = coerceAuthTokens(tokens);

          if (!persistedUser || !normalizedTokens) {
            throw new Error("Login failed - invalid user payload");
          }

          setInMemoryAuthTokens(normalizedTokens);
          const sessionUser = applyPayloadPlatformAdmin(persistedUser);

          // Keep remember-me as a non-sensitive preference only.
          authStorage.setRememberMe(credentials.rememberMe ?? false);

          // Save non-token user data to session storage only.
          authStorage.set(config.storage.keys.user, sessionUser);

          set((state) => {
            state.user = sessionUser;
            state.tokens = normalizedTokens;
            state.platformAdminEntitlement = sessionUser.isPlatformAdmin === true;
            state.isAuthenticated = true;
            state.isLoading = false;
            state.isServiceProvider = false;
            state.serviceProviderTier = null;
          });

          // Fetch project admin status after login without making it part of login success.
          fetchProjectAdminStatusInBackground(
            get().fetchProjectAdminStatus,
            "login.project-admin-status-failed",
          );
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error ? error.message : "Login failed";
            state.isLoading = false;
          });
          throw error;
        }
      },

      verify2FA: async (
        code: string,
        method: "2fa" | "backup_code" = "2fa",
      ) => {
        const { tempUserId } = get();

        if (!tempUserId) {
          throw new Error("No pending 2FA verification");
        }

        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const api = await getApiClient();
          const data = await api.post<
            AuthenticatedAuthResponse,
            { userId: string; token: string; method: "2fa" | "backup_code" }
          >("/auth/2fa/verify", {
            userId: tempUserId,
            token: code,
            method,
          });

          if (!data.data) {
            throw new Error("2FA verification failed - no data received");
          }

          const { user, tokens } = data.data;
          const persistedUser = hydrateStoredUser(user);
          const normalizedTokens = coerceAuthTokens(tokens);

          if (!persistedUser || !normalizedTokens) {
            throw new Error("2FA verification failed - invalid user payload");
          }

          setInMemoryAuthTokens(normalizedTokens);
          const sessionUser = applyPayloadPlatformAdmin(persistedUser);

          authStorage.set(config.storage.keys.user, sessionUser);

          set((state) => {
            state.user = sessionUser;
            state.tokens = normalizedTokens;
            state.platformAdminEntitlement = sessionUser.isPlatformAdmin === true;
            state.isAuthenticated = true;
            state.isLoading = false;
            state.isServiceProvider = false;
            state.requires2FA = false;
            state.tempUserId = null;
            state.twoFactorMethod = null;
          });

          // Fetch project admin status after 2FA login without making it part of login success.
          fetchProjectAdminStatusInBackground(
            get().fetchProjectAdminStatus,
            "verify2FA.project-admin-status-failed",
          );
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "2FA verification failed";
            state.isLoading = false;
          });
          throw error;
        }
      },

      cancel2FA: () => {
        set((state) => {
          state.requires2FA = false;
          state.tempUserId = null;
          state.twoFactorMethod = null;
          state.error = null;
        });
      },

      register: async (data: RegisterData) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const api = await getApiClient();
          const publicAuthChallengeToken =
            await getPublicAuthChallengeToken("register");
          const requestData: RegisterData = publicAuthChallengeToken
            ? { ...data, publicAuthChallengeToken }
            : data;
          const responseData = await api.post<AuthResponse, RegisterData>(
            "/auth/register",
            requestData,
          );

          if (!responseData.data) {
            throw new Error("Registration failed - no data received");
          }

          const registerData = responseData.data;
          const selectedPlan = readSelectedPlan(registerData.selectedPlan);
          const { user, tokens } = registerData;
          const persistedUser = hydrateStoredUser(user);
          const normalizedTokens = coerceAuthTokens(tokens);

          if (isPendingRegistrationResponse(registerData)) {
            const pendingUser = persistedUser ?? undefined;
            authStorage.clearAll();

            set((state) => {
              state.user = null;
              state.tokens = null;
              state.platformAdminEntitlement = false;
              state.isAuthenticated = false;
              state.isLoading = false;
              state.isProjectAdmin = false;
              state.projectAdminIds = [];
              state.isOrgAdmin = false;
              state.isServiceProvider = false;
              state.serviceProviderTier = null;
            });

            const result: PendingRegisterResult = {
              status: "pending_verification",
              email: getResponseEmail(registerData, data.email),
            };

            if (selectedPlan) {
              result.selectedPlan = selectedPlan;
            }

            if (responseData.message) {
              result.message = responseData.message;
            }

            if (pendingUser) {
              result.user = pendingUser;
            }

            return result;
          }

          if (persistedUser && normalizedTokens) {
            setInMemoryAuthTokens(normalizedTokens);
            const sessionUser = applyPayloadPlatformAdmin(persistedUser);

            authStorage.set(config.storage.keys.user, sessionUser);

            set((state) => {
              state.user = sessionUser;
              state.tokens = normalizedTokens;
              state.platformAdminEntitlement =
                sessionUser.isPlatformAdmin === true;
              state.isAuthenticated = true;
              state.isLoading = false;
              state.isServiceProvider = false;
            });

            const result: AuthenticatedRegisterResult = {
              status: "authenticated",
              user: sessionUser,
            };

            if (selectedPlan) {
              result.selectedPlan = selectedPlan;
            }

            if (registerData.sponsorship) {
              result.sponsorship = registerData.sponsorship;
            }

            return result;
          }

          throw new Error("Registration failed - invalid user payload");
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error ? error.message : "Registration failed";
            state.isLoading = false;
          });
          throw error;
        }
      },

      logout: async () => {
        const logoutState = get();
        authDebugWarn("logout.called", {
          initialized: logoutState.initialized,
          isAuthenticated: logoutState.isAuthenticated,
          userPresent: Boolean(logoutState.user),
          stateTokenPresent: Boolean(logoutState.tokens?.accessToken),
          stack: new Error("logout stack").stack,
        });

        try {
          // Pre-logout save hook - ensure all editor changes are saved
          const { useEditorStore } = await import("../editor/editor-store");
          const editorState = useEditorStore.getState();

          if (editorState.project && editorState.isModified) {
            try {
              await editorState.saveProject();
            } catch {
              // Continue with logout even if save fails to prevent user being stuck
            }
          }
        } catch {
          // Continue with logout even if pre-save fails
        }

        // Clear all auth storage (including rememberMe flag)
        authStorage.clearAll();
        resetOrganizationContextForAuthInvalidation();

        set((state) => {
          state.user = null;
          state.tokens = null;
          state.platformAdminEntitlement = false;
          state.isAuthenticated = false;
          state.error = null;
          state.isProjectAdmin = false;
          state.projectAdminIds = [];
          state.isOrgAdmin = false;
          state.isServiceProvider = false;
          state.serviceProviderTier = null;
        });

        // Disconnect sockets and reset chat after auth state is already cleared.
        void import("@/services/api/agentSocketClient")
          .then(({ disconnectAgentSocket }) => disconnectAgentSocket())
          .catch((error) =>
            authDebugWarn(
              "logout.agent-socket-disconnect-failed",
              describeAuthError(error),
            ),
          );

        void import("@/services/websocket/socketClient")
          .then(({ socketClient }) => socketClient.disconnect())
          .catch((error) =>
            authDebugWarn(
              "logout.websocket-disconnect-failed",
              describeAuthError(error),
            ),
          );

        void import("@/stores/agents/chat-store")
          .then(({ useChatStore }) => useChatStore.getState().resetOnLogout())
          .catch((error) =>
            authDebugWarn("logout.chat-reset-failed", describeAuthError(error)),
          );

        // Optionally call logout endpoint
        void getApiClient()
          .then((api) => api.post("/auth/logout"))
          .catch(() => {
            // Ignore errors on logout
          });
      },

      refreshToken: async () => {
        const { tokens } = get();
        const memoryTokens = getInMemoryAuthTokens();
        authDebug("refresh.start", {
          initialized: get().initialized,
          isAuthenticated: get().isAuthenticated,
          isLoading: get().isLoading,
          stateTokenPresent: Boolean(tokens?.accessToken),
          memoryTokenPresent: Boolean(memoryTokens?.accessToken),
          refreshSource: "httpOnly-cookie",
        });

        // Prevent multiple simultaneous refresh attempts
        const state = get();
        if (state.isLoading) {
          authDebug("refresh.wait-existing", {
            stateTokenPresent: Boolean(tokens?.accessToken),
          });
          // Wait for current refresh to complete
          return new Promise((resolve, reject) => {
            const checkInterval = setInterval(() => {
              const currentState = get();
              if (!currentState.isLoading) {
                clearInterval(checkInterval);
                if (currentState.tokens && currentState.tokens !== tokens) {
                  resolve();
                } else {
                  reject(new Error("Token refresh failed"));
                }
              }
            }, 100);
          });
        }

        set((state) => {
          state.isLoading = true;
        });

        try {
          const api = await getApiClient();
          const data = await api.post<RefreshTokenResponse>(
            "/auth/refresh",
            undefined,
            { withCredentials: true },
          );

          if (!data.data) {
            throw new Error("Token refresh failed - no data received");
          }

          const newTokens = setInMemoryAuthTokens(data.data.tokens);
          if (!newTokens) {
            throw new Error("Token refresh failed - invalid token payload");
          }
          authDebug("refresh.success", {
            expiresInMs: newTokens.expiresAt - Date.now(),
            hadRefreshTokenInResponse: false,
          });

          set((state) => {
            if (state.user) {
              state.user = applyServerPlatformAdmin(state.user, false);
            }
            state.tokens = newTokens;
            state.platformAdminEntitlement = false;
            state.isLoading = false;
            state.isServiceProvider = false;
          });

          const { useOrganizationStore } = await import(
            "../organization/organization-store"
          );
          useOrganizationStore.getState().updatePlatformAdminStatus(false);
          fetchProjectAdminStatusInBackground(
            get().fetchProjectAdminStatus,
            "refresh.project-admin-status-failed",
          );

          // Reconnect agent socket with fresh token so it doesn't go stale
          reconnectAgentSocket();
        } catch (error) {
          set((state) => {
            state.isLoading = false;
          });
          authDebugWarn("refresh.failed", describeAuthError(error));
          clearAuthInvalidationPersistence();

          // Callers decide how to recover from refresh failure. Background 401
          // retries should open re-auth instead of clearing the whole session,
          // while startup initialize clears stale non-token state when needed.
          throw error;
        }
      },

      clearError: () => {
        set((state) => {
          state.error = null;
        });
      },

      setUser: (user: User) => {
        const persistedUser = hydrateStoredUser(user);
        if (!persistedUser) {
          throw new Error("Invalid user payload");
        }
        const sessionUser = applyAuthoritativePlatformAdmin(
          persistedUser,
          get().platformAdminEntitlement,
        );
        authStorage.set(config.storage.keys.user, sessionUser);
        set((state) => {
          state.user = sessionUser;
        });
      },

      setTokens: (tokens: AuthTokens) => {
        const normalizedTokens = coerceAuthTokens(tokens);
        if (!normalizedTokens) {
          console.warn(
            "[auth-store] Refusing to persist invalid token payload",
          );
          return;
        }
        setInMemoryAuthTokens(normalizedTokens);
        authDebug("tokens.set", {
          expiresInMs: normalizedTokens.expiresAt - Date.now(),
          hasRefreshToken: Boolean(normalizedTokens.refreshToken),
        });
        set((state) => {
          if (state.user) {
            state.user = applyServerPlatformAdmin(state.user, false);
          }
          state.tokens = normalizedTokens;
          state.platformAdminEntitlement = false;
          state.isAuthenticated = true;
          state.isServiceProvider = false;
        });
      },

      reconcilePlatformAdminEntitlement: (isPlatformAdmin: boolean) => {
        const currentUser = get().user;
        const entitlementUnchanged =
          get().platformAdminEntitlement === isPlatformAdmin;
        const userUnchanged =
          !currentUser || currentUser.isPlatformAdmin === isPlatformAdmin;

        if (entitlementUnchanged && userUnchanged) {
          return;
        }

        const updatedUser = currentUser
          ? applyServerPlatformAdmin(currentUser, isPlatformAdmin)
          : null;
        if (updatedUser) {
          authStorage.set(config.storage.keys.user, updatedUser);
        }
        set((state) => {
          state.platformAdminEntitlement = isPlatformAdmin;
          if (updatedUser) {
            state.user = updatedUser;
          }
        });
      },

      initialize: async () => {
        const state = get();
        if (state.initialized) {
          authDebug("initialize.skip-already-initialized");
          return;
        }
        authDebug("initialize.start", {
          memoryTokenPresent: Boolean(getInMemoryAuthTokens()?.accessToken),
        });

        const legacyTokens = getInMemoryAuthTokens()
          ? null
          : takeLegacyStoredAuthTokens();
        if (legacyTokens) {
          authDebug("initialize.legacy-token-found", {
            expiresInMs: legacyTokens.expiresAt - Date.now(),
          });
          setInMemoryAuthTokens(legacyTokens);
          try {
            await get().refreshToken();
          } catch (error) {
            authDebugWarn(
              "initialize.legacy-refresh-failed",
              describeAuthError(error),
            );
            // Keep the migrated token in memory; expiry validation below still
            // rejects stale credentials, and successful refresh sets the cookie.
          }
        }

        const storedUser = authStorage.get(config.storage.keys.user);
        const user = hydrateStoredUser(storedUser, {
          trustPlatformAdmin: false,
        });
        let tokens = getInMemoryAuthTokens();
        authDebug("initialize.storage-read", {
          storedUserPresent: storedUser !== null,
          userHydrated: Boolean(user),
          tokenPresent: Boolean(tokens?.accessToken),
        });

        if (storedUser !== null && user === null) {
          authDebugWarn("initialize.clear-invalid-user");
          console.warn(
            "[auth-store] Stored auth data failed validation; clearing persisted auth state",
          );
          clearAuthInvalidationPersistence();
          set((state) => {
            state.user = null;
            state.tokens = null;
            state.platformAdminEntitlement = false;
            state.isAuthenticated = false;
            state.initialized = true;
          });
          return;
        }

        if (!tokens && user) {
          try {
            authDebug("initialize.cookie-refresh-attempt");
            await get().refreshToken();
            tokens = getInMemoryAuthTokens();
          } catch (error) {
            authDebugWarn(
              "initialize.cookie-refresh-failed",
              describeAuthError(error),
            );
            tokens = null;
          }
        }

        if (user && tokens) {
          // Validate that we have an access token - refreshToken may be absent in some auth flows
          if (!tokens.accessToken) {
            authDebugWarn("initialize.clear-missing-access-token");
            clearAuthInvalidationPersistence();
            set((state) => {
              state.user = null;
              state.tokens = null;
              state.platformAdminEntitlement = false;
              state.isAuthenticated = false;
              state.initialized = true;
            });
            return;
          }

          // Check if tokens are still valid using expiresAt (absolute timestamp)
          const expirationTime = tokens.expiresAt || 0;
          const isTokenValid = expirationTime > Date.now() + 5 * 60 * 1000; // 5 min buffer

          if (isTokenValid) {
            const sessionUser = applyServerPlatformAdmin(user, false);

            set((state) => {
              state.user = sessionUser;
              state.tokens = tokens;
              state.platformAdminEntitlement = false;
              state.isAuthenticated = true;
              state.initialized = true;
              state.isServiceProvider = false;
              state.serviceProviderTier = null;
            });
            authDebug("initialize.authenticated-with-user", {
              expiresInMs: expirationTime - Date.now(),
              userId: user.id,
            });

            // Fetch project admin status on init without blocking auth restoration.
            fetchProjectAdminStatusInBackground(
              get().fetchProjectAdminStatus,
              "initialize.project-admin-status-failed",
            );
          } else {
            authDebugWarn("initialize.clear-expired-token", {
              expiresInMs: expirationTime - Date.now(),
            });
            console.warn(
              "[auth-store] Token rejected during initialize due to expiry",
            );
            // Clear invalid/expired tokens to prevent auth loops
            clearAuthInvalidationPersistence();
            set((state) => {
              state.user = null;
              state.tokens = null;
              state.platformAdminEntitlement = false;
              state.isAuthenticated = false;
              state.initialized = true;
            });
          }
        } else {
          if (tokens) {
            // Mark the session authenticated so fetchProjectAdminStatus can run,
            // but hold initialized=false until the user profile is restored.
            // Exposing an authenticated-but-userless state to routing deadlocks
            // the post-login redirect (PublicRoute fires performRedirect, which
            // bails on the null user and never retries), pinning the user on a
            // blank spinner. Gating on initialized keeps routing paused until the
            // user is present.
            set((state) => {
              state.user = null;
              state.tokens = tokens;
              state.platformAdminEntitlement = false;
              state.isAuthenticated = true;
            });
            authDebug("initialize.authenticated-token-only", {
              expiresInMs: tokens.expiresAt - Date.now(),
            });
            try {
              await get().fetchProjectAdminStatus();
            } catch (error) {
              authDebugWarn(
                "initialize.token-only-user-fetch-failed",
                describeAuthError(error),
              );
            }
            if (get().user) {
              set((state) => {
                state.initialized = true;
              });
            } else {
              // Token is valid but the user profile could not be restored;
              // treat the session as unusable and fall back to the login form
              // rather than leaving the app authenticated without a user.
              authDebugWarn("initialize.token-only-user-missing");
              clearAuthInvalidationPersistence();
              set((state) => {
                state.user = null;
                state.tokens = null;
                state.platformAdminEntitlement = false;
                state.isAuthenticated = false;
                state.initialized = true;
              });
            }
            return;
          }

          authDebugWarn("initialize.unauthenticated", {
            storedUserPresent: storedUser !== null,
          });
          if (storedUser !== null) {
            clearAuthInvalidationPersistence();
          } else {
            authStorage.remove(ORG_CONTEXT_STORAGE_KEY);
            resetOrganizationContextForAuthInvalidation();
          }
          authStorage.remove(config.storage.keys.tokens);
          set((state) => {
            state.user = null;
            state.tokens = null;
            state.platformAdminEntitlement = false;
            state.isAuthenticated = false;
            state.initialized = true;
          });
        }
      },

      fetchProjectAdminStatus: async () => {
        const { isAuthenticated } = get();
        if (!isAuthenticated) return;

        try {
          const api = await getApiClient();
          const response = await api.get<AuthMeResponse>("/auth/me");
          const authMe = response.data;

          if (authMe) {
            const hydratedUser = authMe.user
              ? hydrateStoredUser(authMe.user, { trustPlatformAdmin: false })
              : null;
            const userPlatformAdmin =
              authMe.user && isObjectLike(authMe.user)
                ? Reflect.get(authMe.user, "isPlatformAdmin")
                : undefined;
            const serverPlatformAdmin =
              typeof userPlatformAdmin === "boolean"
                ? userPlatformAdmin
                : authMe.isPlatformAdmin;
            const authoritativePlatformAdmin = serverPlatformAdmin === true;
            const updatedUser = hydratedUser
              ? applyServerPlatformAdmin(
                  hydratedUser,
                  authoritativePlatformAdmin,
                )
              : null;
            if (updatedUser) {
              authStorage.set(config.storage.keys.user, updatedUser);
            }

            set((state) => {
              state.platformAdminEntitlement = authoritativePlatformAdmin;
              if (updatedUser) {
                state.user = updatedUser;
              } else if (state.user) {
                state.user = applyServerPlatformAdmin(
                  state.user,
                  authoritativePlatformAdmin,
                );
              }
              state.isProjectAdmin = authMe.isProjectAdmin ?? false;
              state.projectAdminIds = authMe.projectAdminIds ?? [];
              state.isOrgAdmin = authMe.isOrgAdmin ?? false;
              state.isServiceProvider =
                authMe.isServiceProvider === true ||
                authMe.isInstaller === true;
              if (!state.isServiceProvider) {
                state.serviceProviderTier = null;
              }
            });
          }
        } catch (error) {
          authDebugWarn(
            "fetch-project-admin-status.failed",
            describeAuthError(error),
          );
          const authDenied = isAuthDenial(error);
          if (authDenied) {
            clearAuthInvalidationPersistence();
          } else {
            authStorage.remove(ORG_CONTEXT_STORAGE_KEY);
            resetOrganizationContextForAuthInvalidation();
          }
          set((state) => {
            if (authDenied) {
              state.user = null;
              state.tokens = null;
              state.platformAdminEntitlement = false;
              state.isAuthenticated = false;
              state.isProjectAdmin = false;
              state.projectAdminIds = [];
              state.isOrgAdmin = false;
              state.isServiceProvider = false;
              state.serviceProviderTier = null;
              return;
            }
            state.platformAdminEntitlement = false;
            if (state.user?.isPlatformAdmin) {
              state.user = applyServerPlatformAdmin(state.user, false);
            }
          });
          // Non-critical — keep the session, but do not preserve stale
          // platform-admin UI state when the live authority is unavailable.
        }
      },

      setHomeProject: async (projectId: string) => {
        const { user } = get();
        if (!user) {
          throw new Error("User not authenticated");
        }

        const ProfileService = await getProfileService();
        const response = await ProfileService.setHomeProject(projectId);

        if (!response.data) {
          throw new Error("Failed to set home project");
        }

        // Deep clone user with new preferences object to ensure Zustand detects change
        const hydratedUser = hydrateStoredUser(response.data, {
          trustPlatformAdmin: false,
        });
        if (!hydratedUser) {
          throw new Error("Failed to hydrate updated user payload");
        }
        const updatedUser = applyAuthoritativePlatformAdmin(
          hydratedUser,
          get().platformAdminEntitlement,
        );
        authStorage.set(config.storage.keys.user, updatedUser);
        set((state) => {
          state.user = {
            ...updatedUser,
            preferences: { ...updatedUser.preferences },
          };
        });
      },

      clearHomeProject: async () => {
        const { user } = get();
        if (!user) {
          throw new Error("User not authenticated");
        }

        const ProfileService = await getProfileService();
        const response = await ProfileService.clearHomeProject();

        if (!response.data) {
          throw new Error("Failed to clear home project");
        }

        // Deep clone user with new preferences object to ensure Zustand detects change
        const hydratedUser = hydrateStoredUser(response.data, {
          trustPlatformAdmin: false,
        });
        if (!hydratedUser) {
          throw new Error("Failed to hydrate updated user payload");
        }
        const updatedUser = applyAuthoritativePlatformAdmin(
          hydratedUser,
          get().platformAdminEntitlement,
        );
        authStorage.set(config.storage.keys.user, updatedUser);
        set((state) => {
          state.user = {
            ...updatedUser,
            preferences: { ...updatedUser.preferences },
          };
        });
      },

      /**
       * Set a specific page as the home page within a project.
       * Sets both homeProjectId and homePageId in user preferences.
       * @param projectId - The project ID
       * @param pageId - The page ID within the project
       * @throws Error if user not authenticated or API fails
       */
      setHomePage: async (projectId: string, pageId: string) => {
        const { user } = get();
        if (!user) {
          throw new Error("User not authenticated");
        }

        try {
          const ProfileService = await getProfileService();
          const response = await ProfileService.setHomePage(projectId, pageId);

          if (!response.data) {
            throw new Error("Failed to set home page");
          }

          // Deep clone user with new preferences object to ensure Zustand detects change
          const hydratedUser = hydrateStoredUser(response.data, {
            trustPlatformAdmin: false,
          });
          if (!hydratedUser) {
            throw new Error("Failed to hydrate updated user payload");
          }
          const updatedUser = applyAuthoritativePlatformAdmin(
            hydratedUser,
            get().platformAdminEntitlement,
          );
          authStorage.set(config.storage.keys.user, updatedUser);
          set((state) => {
            state.user = {
              ...updatedUser,
              preferences: { ...updatedUser.preferences },
            };
          });
        } catch (error) {
          console.error("Failed to set home page:", error);
          throw error;
        }
      },

      /**
       * Clear the home page selection while keeping the home project.
       * Only clears homePageId, leaving homeProjectId intact.
       * @throws Error if user not authenticated or API fails
       */
      clearHomePage: async () => {
        const { user } = get();
        if (!user) {
          throw new Error("User not authenticated");
        }

        try {
          const ProfileService = await getProfileService();
          const response = await ProfileService.clearHomePage();

          if (!response.data) {
            throw new Error("Failed to clear home page");
          }

          // Deep clone user with new preferences object to ensure Zustand detects change
          const hydratedUser = hydrateStoredUser(response.data, {
            trustPlatformAdmin: false,
          });
          if (!hydratedUser) {
            throw new Error("Failed to hydrate updated user payload");
          }
          const updatedUser = applyAuthoritativePlatformAdmin(
            hydratedUser,
            get().platformAdminEntitlement,
          );
          authStorage.set(config.storage.keys.user, updatedUser);
          set((state) => {
            state.user = {
              ...updatedUser,
              preferences: { ...updatedUser.preferences },
            };
          });
        } catch (error) {
          console.error("Failed to clear home page:", error);
          throw error;
        }
      },
    })),
  ),
);

/**
 * Thrown when the access token cannot be refreshed and the caller should
 * surface a re-auth prompt instead of proceeding.
 */
export class SessionExpiredError extends Error {
  constructor(message = "Session expired") {
    super(message);
    this.name = "SessionExpiredError";
  }
}

const ACCESS_TOKEN_REFRESH_BUFFER_SECONDS = 30;

/**
 * Returns a usable access token, refreshing first if the current token is
 * already expired or within the refresh buffer. Throws SessionExpiredError if
 * no token is available and refresh fails (e.g. expired refresh cookie).
 *
 * Use this before emitting on long-lived sockets where the handshake-time
 * identity may have gone stale. HTTP request paths already get this behavior
 * via the axios interceptor.
 */
function readJwtExp(token: string): number | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const padded = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = atob(
      padded.padEnd(padded.length + ((4 - (padded.length % 4)) % 4), "="),
    );
    const raw: unknown = JSON.parse(decoded);
    if (raw && typeof raw === "object" && typeof (raw as { exp?: unknown }).exp === "number") {
      return (raw as { exp: number }).exp;
    }
    return null;
  } catch {
    return null;
  }
}

export async function getValidAccessToken(): Promise<string> {
  const state = useAuthStore.getState();
  const currentToken = state.tokens?.accessToken;
  const expSeconds = currentToken ? readJwtExp(currentToken) : null;
  const nowSeconds = Math.floor(Date.now() / 1000);
  const stale =
    !currentToken ||
    typeof expSeconds !== "number" ||
    expSeconds - nowSeconds <= ACCESS_TOKEN_REFRESH_BUFFER_SECONDS;

  if (!stale && currentToken) {
    return currentToken;
  }

  try {
    await state.refreshToken();
  } catch {
    throw new SessionExpiredError();
  }

  const refreshed = useAuthStore.getState().tokens?.accessToken;
  if (!refreshed) {
    throw new SessionExpiredError();
  }
  return refreshed;
}

// Selectors for common use cases
export const useAuth = () => {
  const { user, isAuthenticated, isLoading, initialized } = useAuthStore();
  return { user, isAuthenticated, isLoading, initialized };
};

export const useAuthActions = () => {
  const { login, register, logout, clearError } = useAuthStore();
  return { login, register, logout, clearError };
};

// Initialize auth store on module load
installAuthDebugHelpers();
const startAuthInitialization = () => {
  void Promise.resolve(useAuthStore.getState().initialize()).catch((error) => {
    authDebugWarn("initialize.auto-start-failed", describeAuthError(error));
  });
};

const syncPlatformAdminEntitlementSnapshot = (
  userId: string | null,
  platformAdminEntitlement: boolean,
): void => {
  setPlatformAdminEntitlementSnapshot({
    userId,
    platformAdminEntitlement,
  });
};

syncPlatformAdminEntitlementSnapshot(
  useAuthStore.getState().user?.id ?? null,
  useAuthStore.getState().platformAdminEntitlement,
);

setTimeout(startAuthInitialization, 0);

// Subscribe to auth changes for side effects (analytics, etc.)
useAuthStore.subscribe(
  (state) =>
    [state.user?.id ?? null, state.platformAdminEntitlement] as const,
  ([userId, platformAdminEntitlement]) => {
    syncPlatformAdminEntitlementSnapshot(userId, platformAdminEntitlement);
  },
);

useAuthStore.subscribe(
  (state) => state.isAuthenticated,
  (isAuthenticated, previousIsAuthenticated) => {
    // Track authentication state changes for analytics
    if (isAuthenticated && !previousIsAuthenticated) {
      // User logged in - dispatch analytics event here if needed
    } else if (!isAuthenticated && previousIsAuthenticated) {
      // User logged out - dispatch analytics event here if needed
    }
  },
);

// HMR: accept updates without full page reload — preserves existing store state
if (import.meta.hot) {
  import.meta.hot.accept();
}
